//TODO REMOVE TS NO CHECK
/* eslint-disable @typescript-eslint/no-var-requires */
import { useAbility } from '@casl/react';
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import { green } from '@mui/material/colors';
import L, { LatLngExpression } from 'leaflet';

import 'leaflet/dist/leaflet.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, TileLayer, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { ACTIONS } from 'src/auth/utils/defineAbilityFor';
import { AbilityContext, Can } from 'src/context/canContext';
import { useDashboard } from 'src/context/dashboardContext';
import { useGetAllApplicationsQuery } from 'src/services/switch-rest-api/application.service';
import { useGetAllMetersQuery } from 'src/services/switch-rest-api/meter.service';
import { useSearchSensorsQuery } from 'src/services/switch-rest-api/sensors.service';
import {
  isAllSelection,
  isAllUtilitySelection,
  toConcreteUtilityType,
} from 'src/utils/selection';
import { tokens } from 'switch-fe-shared/design';
import { Alert } from 'switch-shared/modules/crud/alert/alert.entity';
import {
  AlertSeverity,
  mostSevere,
} from 'switch-shared/modules/crud/alert/alert.enum';
import { UtilityType } from 'switch-shared/modules/crud/utility/utility.enums';

import useEffectiveApplicationGroupId from '../../../hooks/useEffectiveApplicationGroupId';
import useEffectiveApplicationId from '../../../hooks/useEffectiveApplicationId';
import { useSearchGatewaysQuery } from '../../../services/switch-rest-api/gateways.service';
import GatewayPopUp from '../components/GatewayPopUp';
import MeterPopUp from '../components/MeterPopUp';
import ApplicationPopUp, { MeterStatus } from './ApplicationPopUp';
import MapLayersControl from './MapLayersControl';
import SensorPopup from './SensorPopup';

const applicationPinUrl: string =
  require('src/assets/utility/application.svg').default;

const ALERT_BADGE_COLOURS: Record<AlertSeverity, [string, string]> = {
  [AlertSeverity.Info]: [tokens.colors.info, tokens.colors.infoForeground],
  [AlertSeverity.Warning]: [
    tokens.colors.warning,
    tokens.colors.warningForeground,
  ],
  [AlertSeverity.Error]: [
    tokens.colors.destructive,
    tokens.colors.destructiveForeground,
  ],
  [AlertSeverity.Critical]: [
    tokens.colors.destructive,
    tokens.colors.destructiveForeground,
  ],
};

const applicationIcons = new Map<string, L.DivIcon>();

/**
 * Site pin whose buffer circle is centred on the application's coordinates,
 * with a badge counting its alerts coloured by the most severe one.
 */
const applicationIcon = (alerts: Alert[]): L.DivIcon => {
  const severity = mostSevere(alerts.map((alert) => alert.severity));
  const key = `${alerts.length}:${severity}`;
  const cached = applicationIcons.get(key);
  if (cached) {
    return cached;
  }

  const [background, foreground] = severity
    ? ALERT_BADGE_COLOURS[severity]
    : ['', ''];
  const badge = severity
    ? `<span style="
        position: absolute;
        top: -4px;
        right: -2px;
        min-width: 18px;
        height: 18px;
        padding: 0 4px;
        box-sizing: border-box;
        border-radius: 9px;
        border: 2px solid white;
        background: ${background};
        color: ${foreground};
        font: bold 11px/14px system-ui, -apple-system, sans-serif;
        text-align: center;
      ">${alerts.length > 99 ? '99+' : alerts.length}</span>`
    : '';

  const icon = L.divIcon({
    html: `<div style="position: relative; width: 40px; height: 54px;">
      <img src="${applicationPinUrl}" width="40" height="54" alt="" />${badge}
    </div>`,
    className: 'application-pin-icon',
    iconSize: new L.Point(40, 54),
    iconAnchor: new L.Point(20, 42),
    popupAnchor: new L.Point(0, -38),
  });
  applicationIcons.set(key, icon);
  return icon;
};

interface NavigateToLocationEvent extends CustomEvent {
  detail: {
    coordinates: { latitude: number; longitude: number };
    zoom: number;
    itemId: string;
    itemType: string;
    itemName: string;
    openPopup?: boolean;
  };
}

// Component to handle map navigation
const MapNavigationHandler = ({
  onNavigate,
}: {
  onNavigate: (itemId: string, itemType: string, openPopup?: boolean) => void;
}) => {
  const map = useMap();

  useEffect(() => {
    const handleNavigateToLocation = (event: Event) => {
      const customEvent = event as NavigateToLocationEvent;
      const { coordinates, zoom, itemId, itemType, openPopup } =
        customEvent.detail;

      if (coordinates) {
        // Close any open popup immediately before starting navigation
        onNavigate(itemId, itemType, false);

        // Calculate offset to center the marker with popup visible
        const targetPoint = map.project(
          [coordinates.latitude, coordinates.longitude] as LatLngExpression,
          zoom
        );
        // Offset by 75 pixels down to account for popup height
        targetPoint.y -= 75;
        const targetLatLng = map.unproject(targetPoint, zoom);

        map.flyTo(targetLatLng, zoom, {
          duration: 1.5,
          easeLinearity: 0.5,
        });

        if (openPopup && itemId && itemType) {
          // Wait for the map to finish moving before opening popup
          map.once('moveend', () => {
            onNavigate(itemId, itemType, true);
          });
        }
      }
    };

    window.addEventListener('navigate-to-location', handleNavigateToLocation);

    return () => {
      window.removeEventListener(
        'navigate-to-location',
        handleNavigateToLocation
      );
    };
  }, [map, onNavigate]);

  return null;
};

// Component to handle map resize
const MapResizeHandler = () => {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();

    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [map]);

  return null;
};

// Component to handle initial bounds fitting
interface MapBoundsHandlerProps {
  meters?: Array<{
    location?: { coordinates: number[] };
    utilityType?: UtilityType;
  }>;
  gateways?: Array<{ location?: { coordinates: number[] } }>;
  sensors?: Array<{ location?: { coordinates: number[] } }>;
  applications?: Array<{ location?: { coordinates: number[] } }>;
  isDataLoaded?: boolean;
  showGateways?: boolean;
  showMeters?: boolean;
  showElectricityMeters?: boolean;
  showWaterMeters?: boolean;
  showSensors?: boolean;
  showApplications?: boolean;
  isShowingAllUtilities?: boolean;
  utilityKey?: string;
  onResetBounds?: () => void;
}

const MapBoundsHandler = ({
  meters = [],
  gateways = [],
  sensors = [],
  applications = [],
  isDataLoaded = false,
  showGateways = true,
  showMeters = true,
  showElectricityMeters = true,
  showWaterMeters = true,
  showSensors = true,
  showApplications = true,
  isShowingAllUtilities = false,
  utilityKey = '',
  onResetBounds,
}: MapBoundsHandlerProps) => {
  const map = useMap();
  const hasInitialFit = useRef(false);

  const fitBoundsToDevices = () => {
    const metersInBounds = meters.filter((meter) => {
      if (meter.location?.coordinates?.length !== 2) {
        return false;
      }

      if (isShowingAllUtilities) {
        if (meter.utilityType === UtilityType.ELECTRICITY) {
          return showElectricityMeters;
        }
        if (meter.utilityType === UtilityType.WATER) {
          return showWaterMeters;
        }
        return false;
      }

      return showMeters;
    });

    const gatewaysInBounds = showGateways
      ? gateways.filter(
          (gateway) => gateway.location?.coordinates?.length === 2
        )
      : [];

    const sensorsInBounds = showSensors
      ? sensors.filter((sensor) => sensor.location?.coordinates?.length === 2)
      : [];

    const applicationsInBounds = showApplications
      ? applications.filter((app) => app.location?.coordinates?.length === 2)
      : [];

    const allDevices = [
      ...(metersInBounds ?? []),
      ...(gatewaysInBounds ?? []),
      ...(sensorsInBounds ?? []),
      ...applicationsInBounds,
    ];

    if (allDevices.length > 0) {
      const bounds = L.latLngBounds(
        allDevices.map(
          (device) =>
            [
              device.location!.coordinates[1],
              device.location!.coordinates[0],
            ] as LatLngExpression
        )
      );

      // Fit bounds with padding (50 pixels on all sides)
      map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 15, // Don't zoom in too much if there's only one device
        animate: true,
        duration: 1.5,
      });
    }
  };

  useEffect(() => {
    if (!isDataLoaded || hasInitialFit.current) {
      return;
    }

    // Initial bounds fitting
    fitBoundsToDevices();
    hasInitialFit.current = true;
  }, [isDataLoaded, map]);

  useEffect(() => {
    if (!isDataLoaded || !hasInitialFit.current) {
      return;
    }

    fitBoundsToDevices();
  }, [
    isDataLoaded,
    showGateways,
    showMeters,
    showElectricityMeters,
    showWaterMeters,
    showSensors,
    showApplications,
    isShowingAllUtilities,
    utilityKey,
  ]);

  useEffect(() => {
    // Listen for reset bounds event
    const handleResetBounds = () => {
      onResetBounds?.();
      fitBoundsToDevices();
    };

    window.addEventListener('reset-map-bounds', handleResetBounds);

    return () => {
      window.removeEventListener('reset-map-bounds', handleResetBounds);
    };
  }, [map, meters, gateways, sensors, applications, onResetBounds]);

  return null;
};

const DevicesMap = () => {
  /* Hooks & Use Effects */
  const appGroupId = useEffectiveApplicationGroupId();
  const appId = useEffectiveApplicationId();
  const ability = useAbility(AbilityContext);
  const { utility, zone } = useDashboard();
  const theme = useTheme();
  const isAllGroupsSelected = isAllSelection(appGroupId);
  const isAllAppsSelected = isAllSelection(appId);
  const selectedZoneId =
    !isAllAppsSelected && zone?.applicationId === appId ? zone._id : undefined;

  /* States */
  const [showGateways, setShowGateways] = useState(true);
  const [showMeters, setShowMeters] = useState(true);
  const [showElectricityMeters, setShowElectricityMeters] = useState(true);
  const [showWaterMeters, setShowWaterMeters] = useState(true);
  const [showSensors, setShowSensors] = useState(true);
  const [showApplications, setShowApplications] = useState(true);
  const [openPopupId, setOpenPopupId] = useState<{
    id: string;
    type: string;
  } | null>(null);
  const markerRefs = useRef<{ [key: string]: L.Marker | null }>({});

  const isShowingAllUtilities = isAllUtilitySelection(utility);

  /* API Calls */
  const {
    data: meters,
    isLoading: isMetersLoading,
    isFetching: isMetersFetching,
    isSuccess: isMetersSuccess,
    isError: isMetersError,
  } = useGetAllMetersQuery(
    {
      limit: 1000,
      ...(!isAllAppsSelected &&
        appId != null && {
          applicationId: appId,
        }),
      ...(!isAllGroupsSelected && {
        applicationGroupId: appGroupId,
      }),
      ...(selectedZoneId != null && {
        zoneId: selectedZoneId,
      }),
      includeAlerts: true,
      utilityType: toConcreteUtilityType(utility),
    },
    {
      skip:
        appGroupId == null ||
        ability.cannot(ACTIONS.READ, 'Meters') ||
        isAllGroupsSelected,
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: gateways,
    isLoading: isGatewaysLoading,
    isFetching: isGatewaysFetching,
    isSuccess: isGatewaysSuccess,
    isError: isGatewayError,
  } = useSearchGatewaysQuery(
    {
      ...(!isAllAppsSelected &&
        appId != null && {
          applicationId: appId,
        }),
      ...(!isAllGroupsSelected && {
        applicationGroupId: appGroupId,
      }),
      includeAlerts: 'true',
    },
    {
      skip:
        appGroupId == null ||
        ability.cannot(ACTIONS.READ, 'Gateways') ||
        isAllGroupsSelected,
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: sensors,
    isLoading: isSensorsLoading,
    isFetching: isSensorsFetching,
    isSuccess: isSensorsSuccess,
    isError: isSensorsError,
  } = useSearchSensorsQuery(
    {
      ...(!isAllAppsSelected &&
        appId != null && {
          applicationId: appId,
        }),
      ...(!isAllGroupsSelected && {
        applicationGroupId: appGroupId,
      }),
    },
    {
      skip:
        appGroupId == null ||
        ability.cannot(ACTIONS.READ, 'Sensors') ||
        isAllGroupsSelected,
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: applications,
    isLoading: isApplicationsLoading,
    isFetching: isApplicationsFetching,
    isSuccess: isApplicationsSuccess,
  } = useGetAllApplicationsQuery(
    {
      limit: '0',
      applicationGroupId: appGroupId,
      ...(!isAllAppsSelected &&
        appId != null && {
          applicationId: appId,
        }),
    },
    {
      skip:
        appGroupId == null ||
        ability.cannot(ACTIONS.READ, 'Applications') ||
        isAllGroupsSelected,
      refetchOnMountOrArgChange: true,
    }
  );

  const locatedApplications =
    applications?.data.filter(
      (app) => app.location?.coordinates.length === 2
    ) ?? [];

  const alertsByApplication = useMemo(() => {
    const byApplication = new Map<string, Alert[]>();
    const seen = new Set<string>();
    for (const alert of [
      ...(meters?.alerts ?? []),
      ...(gateways?.alerts ?? []),
    ]) {
      const applicationId = alert.scope?.applicationId;
      if (!applicationId || seen.has(alert._id)) {
        continue;
      }
      seen.add(alert._id);
      byApplication.set(applicationId, [
        ...(byApplication.get(applicationId) ?? []),
        alert,
      ]);
    }
    return byApplication;
  }, [meters?.alerts, gateways?.alerts]);

  const meterStatusByApplication = useMemo(() => {
    const byApplication = new Map<string, MeterStatus>();
    for (const meter of meters?.data ?? []) {
      if (!meter.applicationId) {
        continue;
      }
      const status = byApplication.get(meter.applicationId) ?? {
        online: 0,
        offline: 0,
      };
      if (meter.isOnline) {
        status.online++;
      } else {
        status.offline++;
      }
      byApplication.set(meter.applicationId, status);
    }
    return byApplication;
  }, [meters?.data]);

  /* Functions & Functional Components */

  const customMeterIcon = (
    utilityType: UtilityType,
    alerts?: Alert[],
    isOnline?: boolean
  ): L.Icon => {
    switch (utilityType) {
      case UtilityType.ELECTRICITY:
        if ((alerts?.length ?? 0) > 0 || !isOnline) {
          return new L.Icon({
            iconUrl: require('src/assets/utility/elec_alert.svg').default,
            iconSize: new L.Point(40, 47),
          });
        }

        return new L.Icon({
          iconUrl: require('src/assets/utility/elec.svg').default,
          iconSize: new L.Point(40, 47),
        });
      case UtilityType.WATER:
        if ((alerts?.length ?? 0) > 0 || !isOnline) {
          return new L.Icon({
            iconUrl: require('src/assets/utility/water_alert.svg').default,
            iconSize: new L.Point(40, 47),
          });
        }

        return new L.Icon({
          iconUrl: require('src/assets/utility/water.svg').default,
          iconSize: new L.Point(40, 47),
        });
      case UtilityType.GAS:
        if ((alerts?.length ?? 0) > 0 || !isOnline) {
          return new L.Icon({
            iconUrl: require('src/assets/utility/gas_alert.svg').default,
            iconSize: new L.Point(40, 47),
          });
        }

        return new L.Icon({
          iconUrl: require('src/assets/utility/gas.svg').default,
          iconSize: new L.Point(40, 47),
        });
      default:
        return new L.Icon({
          iconUrl: require('src/assets/utility/elec.svg').default,
          iconSize: new L.Point(40, 47),
        });
    }
  };

  const customGatewayIcon = (alerts: Alert[], isOnline?: boolean): L.Icon => {
    if ((alerts?.length ?? 0) > 0 || !isOnline) {
      return new L.Icon({
        iconUrl: require('src/assets/utility/gateway_alert.svg').default,
        iconSize: new L.Point(40, 47),
      });
    }

    return new L.Icon({
      iconUrl: require('src/assets/utility/gateway.svg').default,
      iconSize: new L.Point(40, 47),
    });
  };

  const customSensorIcon = (alerts: Alert[], isOnline?: boolean): L.Icon => {
    if ((alerts?.length ?? 0) > 0 || !isOnline) {
      return new L.Icon({
        iconUrl: require('src/assets/utility/sensor_alert.svg').default,
        iconSize: new L.Point(40, 47),
      });
    }

    return new L.Icon({
      iconUrl: require('src/assets/utility/sensor.svg').default,
      iconSize: new L.Point(40, 47),
    });
  };

  const createUnifiedClusterCustomIcon = (cluster: any) => {
    const markers = cluster.getAllChildMarkers();

    // Determine the highest severity among all markers in the cluster
    // Check meters, gateways, and sensors
    let hasError = false;
    let hasWarning = false;

    markers.forEach((marker: any) => {
      // Check meter alerts and offline status
      const meterId = marker.options?.meterId;
      if (meterId) {
        // Check if meter is offline - treat as error
        const meterData = meters?.data?.find((m) => m._id === meterId);
        if (meterData && !meterData.isOnline) {
          hasError = true;
        }

        // Check meter alerts
        if (meters?.alerts) {
          const meterAlerts = meters.alerts.filter(
            (alert) =>
              //@ts-ignore
              alert.cause.meterId === meterId
          );

          meterAlerts.forEach((alert) => {
            if (
              alert.severity === AlertSeverity.Error ||
              alert.severity === AlertSeverity.Critical
            ) {
              hasError = true;
            } else if (alert.severity === AlertSeverity.Warning) {
              hasWarning = true;
            }
          });
        }
      }

      // Check gateway alerts and offline status
      const gatewayIMEI = marker.options?.gatewayIMEI;
      if (gatewayIMEI) {
        // Check if gateway is offline - treat as error
        const gatewayData = gateways?.results?.find(
          (g) => g.imei === gatewayIMEI
        );
        if (gatewayData && !gatewayData.isOnline) {
          hasError = true;
        }

        // Check gateway alerts
        if (gateways?.alerts) {
          const gatewayAlerts = gateways.alerts.filter(
            (alert) =>
              //@ts-ignore
              alert.cause.gatewayIMEI === gatewayIMEI
          );

          gatewayAlerts.forEach((alert) => {
            if (
              alert.severity === AlertSeverity.Error ||
              alert.severity === AlertSeverity.Critical
            ) {
              hasError = true;
            } else if (alert.severity === AlertSeverity.Warning) {
              hasWarning = true;
            }
          });
        }
      }

      // Sensors don't have alerts yet, so we skip checking them
    });

    // Determine cluster color based on severity
    let backgroundColor: string = green[400]; // Default
    let borderColor: string = green[600];

    if (hasError) {
      backgroundColor = theme.tokens.colors.destructive; // Red for error/critical
      borderColor = '#d32f2f';
    } else if (hasWarning) {
      backgroundColor = '#ff9800'; // Orange for warning
      borderColor = '#f57c00';
    }

    const count = cluster.getChildCount();

    return L.divIcon({
      html: `<div style="
        background-color: ${backgroundColor};
        width: 40px;
        height: 40px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 3px solid ${borderColor};
        box-shadow: 0 3px 8px rgba(0,0,0,0.4);
        font-weight: bold;
        color: white;
        font-size: 16px;
        font-family: system-ui, -apple-system, sans-serif;
      ">${count}</div>`,
      className: 'custom-cluster-icon',
      iconSize: L.point(40, 40, true),
    });
  };

  const getCenterCoords = () => {
    const allDevices = [
      ...(meters?.data?.filter((ele) => ele.location?.coordinates.length) ??
        []),
      ...(gateways?.results?.filter(
        (ele) => ele.location?.coordinates.length
      ) ?? []),
      ...(sensors?.data?.filter((ele) => ele.location?.coordinates.length) ??
        []),
      ...locatedApplications,
    ];

    const sumCoordinates = allDevices.reduce(
      (acc, item) => {
        if (item.location?.coordinates) {
          if (
            !item.location?.coordinates[0] ||
            !item.location?.coordinates[1]
          ) {
          }
          acc.x += item.location?.coordinates[1] ?? 0;
          acc.y += item.location?.coordinates[0] ?? 0;
        }
        return acc;
      },
      { x: 0, y: 0 }
    );

    if (allDevices.length) {
      return [
        sumCoordinates.x / allDevices.length,
        sumCoordinates.y / allDevices.length,
      ] as LatLngExpression;
    }

    return [-28.739154, 24.758499] as LatLngExpression;
  };

  const handleNavigate = (
    itemId: string,
    itemType: string,
    openPopup?: boolean
  ) => {
    if (openPopup) {
      setOpenPopupId({ id: itemId, type: itemType });
      // Open the popup using the marker ref
      const marker = markerRefs.current[itemId];
      if (marker) {
        marker.openPopup();
      }
    } else {
      // Close any currently open popup
      if (openPopupId && markerRefs.current[openPopupId.id]) {
        markerRefs.current[openPopupId.id]?.closePopup();
      }
      setOpenPopupId(null);
    }
  };

  const handleResetBounds = () => {
    // Close any open popup
    if (openPopupId && markerRefs.current[openPopupId.id]) {
      markerRefs.current[openPopupId.id]?.closePopup();
    }
    setOpenPopupId(null);
  };

  const isDataLoaded =
    !isMetersLoading &&
    !isMetersFetching &&
    !isGatewaysLoading &&
    !isGatewaysFetching &&
    !isSensorsLoading &&
    !isSensorsFetching &&
    !isApplicationsLoading &&
    !isApplicationsFetching;

  if (isAllGroupsSelected) {
    return (
      <Grid
        container
        width={'100%'}
        height={'100%'}
        justifyContent={'center'}
        alignItems={'center'}
        px={2}
      >
        <Grid item>
          <Typography variant='body2' color='text.secondary' textAlign='center'>
            Select a specific application group to view the device map.
          </Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      {!isDataLoaded ? (
        <Grid
          container
          width={'100%'}
          height={'100%'}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <Grid item>
            <CircularProgress />
          </Grid>
        </Grid>
      ) : (
        <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
          <MapContainer
            center={getCenterCoords()}
            zoom={6}
            scrollWheelZoom={true}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap contributors</a>'
              url='https://tile.openstreetmap.org/{z}/{x}/{y}.png'
            />
            <MarkerClusterGroup
              chunkedLoading
              iconCreateFunction={createUnifiedClusterCustomIcon}
              disableClusteringAtZoom={17}
            >
              <MapNavigationHandler onNavigate={handleNavigate} />
              <MapResizeHandler />
              <MapBoundsHandler
                meters={meters?.data}
                gateways={gateways?.results}
                sensors={sensors?.data}
                applications={locatedApplications}
                isDataLoaded={isDataLoaded}
                showGateways={showGateways}
                showMeters={showMeters}
                showElectricityMeters={showElectricityMeters}
                showWaterMeters={showWaterMeters}
                showSensors={showSensors}
                showApplications={showApplications}
                isShowingAllUtilities={isShowingAllUtilities}
                utilityKey={utility}
                onResetBounds={handleResetBounds}
              />
              {(showMeters ||
                (isShowingAllUtilities &&
                  (showElectricityMeters || showWaterMeters))) &&
                isMetersSuccess &&
                meters?.data
                  .filter((ele) => {
                    if (
                      !ele.location?.coordinates.length ||
                      ele.location.coordinates.length !== 2
                    ) {
                      return false;
                    }

                    if (isShowingAllUtilities) {
                      if (ele.utilityType === UtilityType.ELECTRICITY) {
                        return showElectricityMeters;
                      } else if (ele.utilityType === UtilityType.WATER) {
                        return showWaterMeters;
                      }
                      return false;
                    }

                    return showMeters;
                  })
                  .map((meter) => (
                    <Marker
                      icon={customMeterIcon(
                        meter.utilityType,
                        meters.alerts?.filter(
                          (ele) =>
                            //@ts-ignore
                            ele.cause.meterId === meter._id
                        ),
                        meter.isOnline
                      )}
                      key={meter._id}
                      position={
                        [
                          meter.location!.coordinates[1],
                          meter.location!.coordinates[0],
                        ] as LatLngExpression
                      }
                      //@ts-ignore
                      meterId={meter._id}
                      ref={(ref) => {
                        markerRefs.current[meter._id] = ref;
                      }}
                    >
                      <MeterPopUp
                        meter={meter}
                        alerts={meters.alerts?.filter(
                          (ele) =>
                            //@ts-ignore
                            ele.cause.meterId === meter._id
                        )}
                        meterPoints={meters.meterPoints}
                      />
                    </Marker>
                  ))}
              {showGateways &&
                isGatewaysSuccess &&
                gateways?.results
                  .filter((ele) => ele.location?.coordinates.length === 2)
                  .map((gateway) => (
                    <Marker
                      icon={customGatewayIcon(
                        gateways.alerts?.filter(
                          (ele) =>
                            //@ts-ignore
                            ele.cause.gatewayIMEI === gateway.imei
                        ) ?? [],
                        gateway.isOnline
                      )}
                      key={gateway._id}
                      position={
                        [
                          gateway.location!.coordinates[1],
                          gateway.location!.coordinates[0],
                        ] as LatLngExpression
                      }
                      //@ts-ignore
                      gatewayIMEI={gateway.imei}
                      ref={(ref) => {
                        markerRefs.current[gateway._id] = ref;
                      }}
                    >
                      <GatewayPopUp
                        gateway={gateway}
                        alerts={gateways.alerts?.filter(
                          (ele) =>
                            //@ts-ignore
                            ele.cause.gatewayIMEI === gateway.imei
                        )}
                      />
                    </Marker>
                  ))}
              <Can I={ACTIONS.MANAGE} a={'all'}>
                {showSensors &&
                  isSensorsSuccess &&
                  sensors.data
                    .filter((s) => s.location?.coordinates.length === 2)
                    .map((sensor) => (
                      <Marker
                        icon={customSensorIcon([], true)}
                        key={sensor._id}
                        position={
                          [
                            sensor.location!.coordinates[1],
                            sensor.location!.coordinates[0],
                          ] as LatLngExpression
                        }
                        ref={(ref) => {
                          markerRefs.current[sensor._id] = ref;
                        }}
                      >
                        <SensorPopup sensor={sensor} />
                      </Marker>
                    ))}
              </Can>
            </MarkerClusterGroup>
            {showApplications &&
              isApplicationsSuccess &&
              locatedApplications.map((application) => (
                <Marker
                  icon={applicationIcon(
                    alertsByApplication.get(application._id) ?? []
                  )}
                  key={application._id}
                  position={
                    [
                      application.location!.coordinates[1],
                      application.location!.coordinates[0],
                    ] as LatLngExpression
                  }
                  zIndexOffset={-1000}
                  ref={(ref) => {
                    markerRefs.current[application._id] = ref;
                  }}
                >
                  <ApplicationPopUp
                    application={application}
                    group={applications.applicationGroups?.find(
                      (group) => group._id === application.applicationGroupId
                    )}
                    alerts={alertsByApplication.get(application._id) ?? []}
                    meterStatus={
                      isMetersSuccess
                        ? (meterStatusByApplication.get(application._id) ?? {
                            online: 0,
                            offline: 0,
                          })
                        : undefined
                    }
                  />
                </Marker>
              ))}
          </MapContainer>
          <MapLayersControl
            showMeters={showMeters}
            showElectricityMeters={showElectricityMeters}
            showWaterMeters={showWaterMeters}
            showGateways={showGateways}
            showSensors={showSensors}
            showApplications={showApplications}
            onToggleMeters={() => setShowMeters(!showMeters)}
            onToggleElectricityMeters={() =>
              setShowElectricityMeters(!showElectricityMeters)
            }
            onToggleWaterMeters={() => setShowWaterMeters(!showWaterMeters)}
            onToggleGateways={() => setShowGateways(!showGateways)}
            onToggleSensors={() => setShowSensors(!showSensors)}
            onToggleApplications={() => setShowApplications(!showApplications)}
          />
        </Box>
      )}
    </>
  );
};

export default DevicesMap;
